import { sortDataLayers } from '@//utils/layers/dataLayers'
import { requestMetadata } from '@//utils/layers/metadata'
import {
  IBaseDataLayer,
  IDataLayer,
  ILayerPack,
  ILoadedDataLayer,
  IRasterFeature,
  IVisibleDataLayer,
  SimpleGeoJSONFeature,
  SimpleLayerSpecification,
  TLayerMetadata
} from '@lifk/types'
import { ExpressionSpecification, LngLat } from 'mapbox-gl'
import {
  computed,
  ComputedRef,
  inject,
  InjectionKey,
  nextTick,
  provide,
  Ref,
  ref
} from 'vue'

type TDataLayerVisibilityStore = {
  /** What layers are currently selected and showing in the legend? */
  loadedDataLayers: ComputedRef<ILoadedDataLayer[]>
  /** What layer packs are currently selected and showing in the legend? */
  activeLayerPacks: ComputedRef<ILayerPack[]>
  /** What data layers, layer values, and mapbox layers are currently visible? */
  visibleLayers: ComputedRef<IVisibleDataLayer[]>
  /** Cached data layer metadata */
  loadedMetadata: ComputedRef<TLayerMetadata>
  /** LngLat of the most recent click event on the map. If defined, popover will show. */
  lastClickedLngLat: Ref<LngLat | undefined>
  /** What vector features are currently active (clicked)? */
  activeVectorFeatures: Ref<SimpleGeoJSONFeature[]>
  /** What raster values are currently active (clicked)? (May include undefined / no data) */
  activeRasterFeatures: Ref<IRasterFeature[]>
  clearActiveFeatures: () => void
  isLoaded: (dataLayer: IDataLayer) => boolean
  addDataLayers: (dataLayers: IDataLayer[]) => void
  removeDataLayer: (dataLayerKey: string) => void
  toggleDataLayer: (dataLayer: IDataLayer) => void
  toggleDataLayerVisibility: (toggle: ILoadedDataLayer) => void
  toggleLayerValueVisibility: (
    loaded: ILoadedDataLayer,
    layerValueKeys: string[]
  ) => void
  updateLayerFilter: (
    loaded: ILoadedDataLayer,
    filter: ExpressionSpecification | undefined
  ) => void
  toggleLayerPack: (
    layerPack: ILayerPack,
    dataLayers: IDataLayer[],
    clearOthers: boolean
  ) => void
  updateMetadata: (additionalDataLayers: IBaseDataLayer[]) => void
}
export const DataLayerVisibilityKey =
  Symbol() as InjectionKey<TDataLayerVisibilityStore>

export const provideDataLayerVisibility = (): {
  visibleLayers: ComputedRef<IVisibleDataLayer[]>
} => {
  const loadedDataLayersRef = ref<ILoadedDataLayer[]>([]) // private
  const loadedDataLayers = computed(() => loadedDataLayersRef.value) // readonly

  const activeLayerPacksRef = ref<ILayerPack[]>([]) // private
  const activeLayerPacks = computed(() => activeLayerPacksRef.value) // readonly

  const loadedMetadataRef = ref<TLayerMetadata>({}) // private
  const loadedMetadata = computed(() => loadedMetadataRef.value) // readonly

  const lastClickedLngLat = ref<LngLat | undefined>()
  const activeVectorFeatures = ref<SimpleGeoJSONFeature[]>([])
  const activeRasterFeatures = ref<IRasterFeature[]>([])

  const visibleLayers = computed(() =>
    loadedDataLayers.value
      .filter(({ hide }) => !hide)
      .flatMap((loaded) =>
        loaded.dataLayer.mapLayers.map(({ mapboxLayers, layerValues }) => {
          const visibleLayerValues = layerValues.filter(
            ({ key }) => loaded.hideLayerValues.indexOf(key) === -1
          )

          let visibleMapboxLayers: SimpleLayerSpecification[] = mapboxLayers
          if (visibleLayerValues.length === 0) {
            // if no layer values are visible for a mapLayer, don't add the mapboxLayers
            visibleMapboxLayers = []
          } else if (visibleLayerValues.length < layerValues.length) {
            // if some layerValues are visible, add filters to mapboxLayers
            visibleMapboxLayers = mapboxLayers.map((mapboxLayer) => {
              let newFilter: ExpressionSpecification = ['any']
              visibleLayerValues.forEach((lv) => {
                const filter = mapboxLayer.id.endsWith('.label')
                  ? lv.labelConfig.filter
                  : lv.layerConfig.filter
                if (filter) {
                  newFilter.push(filter)
                }
              })
              // if there's already a filter, add to it
              if (mapboxLayer.filter) {
                newFilter = ['all', mapboxLayer.filter, newFilter]
              }
              return {
                ...mapboxLayer,
                filter: newFilter
              } as SimpleLayerSpecification
            })
          }

          // add any additional custom filters
          if (loaded.filter) {
            visibleMapboxLayers = visibleMapboxLayers.map(
              (mapboxLayer) =>
                ({
                  ...mapboxLayer,
                  filter:
                    mapboxLayer.filter && loaded.filter?.[0] !== 'in'
                      ? ['all', mapboxLayer.filter, loaded.filter]
                      : loaded.filter
                }) as SimpleLayerSpecification
            )
          }

          return {
            dataLayer: loaded.dataLayer,
            mapboxLayers: visibleMapboxLayers,
            layerValues: visibleLayerValues // already sorted by zindex in transformDataLayers.ts
          }
        })
      )
  )

  const clearActiveFeatures = () => {
    lastClickedLngLat.value = undefined
    activeVectorFeatures.value = []
    activeRasterFeatures.value = []
  }

  const isLoaded = (dataLayer: IDataLayer) =>
    !!loadedDataLayersRef.value.find((d) => d.dataLayer.key === dataLayer.key)

  const addDataLayer = (dataLayer: IDataLayer) => {
    // Don't double-add data layers
    if (isLoaded(dataLayer)) return

    // Sort data layers here to ensure proper render order on the map
    const newLoaded: ILoadedDataLayer[] = sortDataLayers([
      ...loadedDataLayersRef.value,
      { dataLayer, hide: false, hideLayerValues: [] }
    ])

    loadedDataLayersRef.value = newLoaded
  }

  const addDataLayers = (dataLayers: IDataLayer[]) => {
    dataLayers.forEach((dl) => {
      addDataLayer(dl)
    })
    updateMetadata()
  }

  const removeDataLayer = (dataLayerKey: string) => {
    loadedDataLayersRef.value = loadedDataLayersRef.value.filter(
      (loaded) => loaded.dataLayer.key !== dataLayerKey
    )

    if (loadedDataLayersRef.value.length === 0) {
      removeDataLayers()
    } else {
      // remove layer pack if layer is a member
      const layerPackParent = activeLayerPacksRef.value.find((lp) =>
        lp.dataLayers?.some((dl) => dl === dataLayerKey)
      )
      if (layerPackParent) {
        activeLayerPacksRef.value = activeLayerPacksRef.value.filter(
          (lp) => lp._id !== layerPackParent._id
        )
      }
    }
  }

  const removeDataLayers = () => {
    loadedDataLayersRef.value = []

    clearActiveFeatures()
    activeLayerPacksRef.value = []
  }

  const toggleDataLayer = (dataLayer: IDataLayer) => {
    if (isLoaded(dataLayer)) {
      removeDataLayer(dataLayer.key)
    } else {
      addDataLayer(dataLayer)
      updateMetadata()
    }
  }

  const replaceDataLayer = (replacement: ILoadedDataLayer) => {
    if (!loadedDataLayersRef.value) return
    loadedDataLayersRef.value = loadedDataLayersRef.value.map((loaded) =>
      loaded.dataLayer.key === replacement.dataLayer.key ? replacement : loaded
    )
  }

  const addLayerPack = (layerPack: ILayerPack, dataLayers: IDataLayer[]) => {
    activeLayerPacksRef.value.push(layerPack)
    dataLayers.forEach((layer) => {
      addDataLayer(layer)
    })
  }

  const removeLayerPack = (layerPackId: string) => {
    const layerPack = activeLayerPacks.value.find(
      (lp) => lp._id === layerPackId
    )

    // Set of data layer keys that are active in other (still active) layer packs
    const otherActiveLPDataLayers = new Set(
      activeLayerPacks.value
        .filter(({ _id }) => _id !== layerPackId)
        .flatMap(({ dataLayers }) => dataLayers)
    )
    layerPack?.dataLayers?.forEach((layer) => {
      // only remove data layers if they are not used in other active layer packs
      if (!otherActiveLPDataLayers.has(layer)) removeDataLayer(layer)
    })
  }

  const toggleLayerPack = (
    layerPack: ILayerPack,
    dataLayers: IDataLayer[],
    clearOthers: boolean
  ) => {
    if (activeLayerPacks.value.some((lp) => lp._id === layerPack._id)) {
      removeLayerPack(layerPack._id)
    } else if (clearOthers) {
      removeDataLayers()
      // allow the map to update before adding new layers
      nextTick(() => {
        addLayerPack(layerPack, dataLayers)
      })
    } else {
      addLayerPack(layerPack, dataLayers)
    }
  }

  const updateMetadata = async (
    additionalDataLayers: IBaseDataLayer[] = []
  ) => {
    const newMetadata = await requestMetadata([
      ...loadedDataLayersRef.value.map((loaded) => loaded.dataLayer),
      ...additionalDataLayers
    ])
    // update metadata non-destructively to cache values that have been fetched previously
    loadedMetadataRef.value = { ...loadedMetadataRef.value, ...newMetadata }
  }

  const toggleDataLayerVisibility = (toggle: ILoadedDataLayer) => {
    replaceDataLayer({
      ...toggle,
      hide: !toggle.hide
    })
  }

  const toggleLayerValueVisibility = (
    loaded: ILoadedDataLayer,
    layerValueKeys: string[]
  ) => {
    let hideLayerValues = [...loaded.hideLayerValues]

    layerValueKeys.forEach((layerValueKey) => {
      if (!hideLayerValues.includes(layerValueKey)) {
        hideLayerValues.push(layerValueKey)
      } else {
        hideLayerValues = hideLayerValues.filter((id) => id !== layerValueKey)
      }
    })

    replaceDataLayer({
      ...loaded,
      hideLayerValues
    })
  }

  const updateLayerFilter = (
    loaded: ILoadedDataLayer,
    filter: ExpressionSpecification | undefined
  ) => {
    replaceDataLayer({
      ...loaded,
      filter
    })
  }

  provide(DataLayerVisibilityKey, {
    loadedDataLayers,
    activeLayerPacks,
    visibleLayers,
    loadedMetadata,
    lastClickedLngLat,
    activeVectorFeatures,
    activeRasterFeatures,
    clearActiveFeatures,
    isLoaded,
    addDataLayers,
    removeDataLayer,
    toggleDataLayer,
    toggleDataLayerVisibility,
    toggleLayerValueVisibility,
    updateLayerFilter,
    toggleLayerPack,
    updateMetadata
  })

  return { visibleLayers }
}

// Use in scenarios that may exist outside of Map context (ie- tooltips, references)
export function useOptionalDataLayerVisibility():
  | TDataLayerVisibilityStore
  | undefined {
  return inject(DataLayerVisibilityKey, undefined)
}

// Use for scenarios that are guaranteed to exist within Map context (most cases)
export default function useDataLayerVisibility(): TDataLayerVisibilityStore {
  return inject(DataLayerVisibilityKey)!
}
